import type { UserState } from './types';
import { defaultUserState } from './types';
import { userSchema } from './schema';

const DB_NAME = 'iaea-npt27-reader';
const DB_VERSION = 1;
const STATE_STORE = 'state';
const STATE_KEY = 'user-state';
const BACKUP_FORMAT = 'npt27-reader-backup';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB недоступна в этом браузере'));
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => { dbPromise = null; reject(request.error ?? new Error('Не удалось открыть локальное хранилище')); };
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STATE_STORE)) db.createObjectStore(STATE_STORE);
    };
    request.onsuccess = () => resolve(request.result);
  });
  return dbPromise;
}

function transaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore, done: (value: T) => void, fail: (error: Error) => void) => void): Promise<T> {
  return openDatabase().then((db) => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STATE_STORE, mode);
    const store = tx.objectStore(STATE_STORE);
    let settled = false;
    let result: T;
    const done = (value: T) => { result = value; };
    const fail = (error: Error) => { if (!settled) { settled = true; reject(error); } };
    tx.onerror = () => fail(tx.error ?? new Error('Операция локального хранилища завершилась ошибкой'));
    tx.onabort = () => fail(tx.error ?? new Error('Запись отменена; данные не сохранены'));
    tx.oncomplete = () => { if (!settled) { settled = true; resolve(result); } };
    try { action(store, done, fail); } catch (error) { tx.abort(); fail(error instanceof Error ? error : new Error(String(error))); }
  }));
}

function mergeDefaults(value: Partial<UserState>, contentVersion: string): UserState {
  if(value.schemaVersion!==undefined&&value.schemaVersion!==1)throw new Error('Версия пользовательской базы несовместима. Запись заблокирована.');
  const fallback = defaultUserState(contentVersion);
  return {
    ...fallback,
    ...value,
    schemaVersion: 1,
    settings: { ...fallback.settings, ...(value.settings ?? {}) },
    readingPosition: { ...fallback.readingPosition, ...(value.readingPosition ?? {}) },
    readingProgress: { ...fallback.readingProgress, ...(value.readingProgress ?? {}) },
    backupStatus: { ...fallback.backupStatus, ...(value.backupStatus ?? {}) },
    annotations: Array.isArray(value.annotations) ? value.annotations : [],
    bookmarks: Array.isArray(value.bookmarks) ? value.bookmarks : [],
    vocabulary: Array.isArray(value.vocabulary) ? value.vocabulary : [],
    reviewEvents: Array.isArray(value.reviewEvents) ? value.reviewEvents : [],
  } as UserState;
}

export async function loadState(contentVersion: string): Promise<UserState> {
  const stored = await transaction<UserState | undefined>('readonly', (store, done, fail) => {
    const request = store.get(STATE_KEY);
    request.onsuccess = () => done(request.result as UserState | undefined);
    request.onerror = () => fail(request.error ?? new Error('Не удалось прочитать локальное состояние'));
  });
  return mergeDefaults(stored ?? {}, contentVersion);
}

export async function saveState(state: UserState): Promise<void> {
  await transaction<void>('readwrite', (store, done, fail) => {
    const request = store.put(state, STATE_KEY);
    request.onsuccess = () => done(undefined);
    request.onerror = () => fail(request.error ?? new Error('Не удалось сохранить локальное состояние'));
  });
}

/** Read-modify-write in one transaction: concurrent tabs operate on latest entities. */
export async function commitState(updater: (current: UserState) => UserState, contentVersion: string, checkpoint = false): Promise<UserState> {
  return transaction<UserState>('readwrite', (store, done, fail) => {
    const read = store.get(STATE_KEY);
    read.onsuccess = () => {
      try {
        const current = mergeDefaults(read.result ?? {}, contentVersion);
        const next = { ...updater(current), revision: current.revision + 1 };
        userSchema.parse(next);
        if (checkpoint) {
          const key=`checkpoint-${randomId('state')}`;
          store.put(current,key);
          const draft=store.get('note-draft');draft.onsuccess=()=>store.put(draft.result??null,`${key}-draft`);
        }
        if(next.pendingDraft){store.put(next.pendingDraft,'note-draft');delete next.pendingDraft;}
        const put = store.put(next, STATE_KEY);
        put.onsuccess = () => done(next);
        put.onerror = () => fail(put.error ?? new Error('Запись не выполнена'));
      } catch (error) { store.transaction.abort(); fail(error instanceof Error ? error : new Error(String(error))); }
    };
    read.onerror = () => fail(read.error ?? new Error('Чтение не выполнено'));
  });
}

export function mergeStates(current: UserState, incoming: UserState): UserState {
  const merged = { ...current };
  for (const name of ['annotations','bookmarks','vocabulary','reviewEvents'] as const) {
    const map = new Map<string, {id:string}>(current[name].map(x=>[x.id,x]));
    for (const item of incoming[name]) {
      const old=map.get(item.id);
      if (!old) map.set(item.id,item);
      else if (JSON.stringify(old)!==JSON.stringify(item)) {
        // Deterministic variant identity makes repeated conflicting imports idempotent.
        const serialized=JSON.stringify(item); let h=2166136261;
        for(let i=0;i<serialized.length;i++) h=Math.imul(h^serialized.charCodeAt(i),16777619);
        const base=`${item.id}-conflict-${(h>>>0).toString(16)}`;
        let conflictId=base,counter=0;
        while(map.has(conflictId)&&JSON.stringify({...map.get(conflictId),id:item.id})!==serialized)conflictId=`${base}-${++counter}`;
        map.set(conflictId,{...item,id:conflictId});
      }
    }
    Object.assign(merged,{[name]:[...map.values()]});
  }
  merged.readingProgress={visitedSectionIds:[...new Set([...current.readingProgress.visitedSectionIds,...incoming.readingProgress.visitedSectionIds])],explicitCompletedSectionIds:[...new Set([...current.readingProgress.explicitCompletedSectionIds,...incoming.readingProgress.explicitCompletedSectionIds])]};
  return merged;
}

function randomId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `${prefix}-${uuid ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}

async function sha256(value: string): Promise<string> {
  if (globalThis.crypto?.subtle) {
    const bytes = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('Криптографический API недоступен');
}

export interface BackupFile {
  formatId: typeof BACKUP_FORMAT;
  schemaVersion: 1;
  backupId: string;
  createdAt: string;
  appVersion: string;
  checksum: string;
  payload: UserState;
}

export async function createBackup(state: UserState, includeCurrentDraft=true): Promise<BackupFile> {
  const payload = JSON.parse(JSON.stringify(state)) as UserState;
  if(includeCurrentDraft&&!payload.pendingDraft&&typeof indexedDB!=='undefined'){
    try{const draft=await loadDraft<UserState['pendingDraft']>();if(draft)payload.pendingDraft=draft;}catch{/* The caller can still export its confirmed in-memory state. */}
  }
  const canonical = JSON.stringify(payload);
  return {
    formatId: BACKUP_FORMAT,
    schemaVersion: 1,
    backupId: randomId('backup'),
    createdAt: new Date().toISOString(),
    appVersion: '0.2.0',
    checksum: await sha256(canonical),
    payload,
  };
}

function assertStringLength(value: unknown, max: number, label: string) {
  if (typeof value === 'string' && value.length > max) throw new Error(`Поле ${label} слишком длинное`);
}

export async function parseBackup(text: string, contentVersion: string): Promise<UserState> {
  if (text.length > 25 * 1024 * 1024) throw new Error('Файл резервной копии больше 25 МБ');
  let parsed: Partial<BackupFile>;
  try { parsed = JSON.parse(text) as Partial<BackupFile>; } catch { throw new Error('Файл не является корректным JSON'); }
  if (!parsed || parsed.formatId !== BACKUP_FORMAT || parsed.schemaVersion !== 1 || !parsed.payload || typeof parsed.checksum !== 'string') {
    throw new Error('Неподдерживаемый формат резервной копии');
  }
  const canonical = JSON.stringify(parsed.payload);
  if (await sha256(canonical) !== parsed.checksum) throw new Error('Контрольная сумма резервной копии не совпадает');
  const valid=userSchema.safeParse(parsed.payload);
  if (!valid.success) throw new Error(`Некорректная структура backup: ${valid.error.issues[0]?.path.join('.')}`);
  if (!Array.isArray(parsed.payload.annotations) || !Array.isArray(parsed.payload.vocabulary)) throw new Error('В резервной копии нет ожидаемых коллекций');
  if (parsed.payload.annotations.length > 20000 || parsed.payload.vocabulary.length > 10000) throw new Error('Слишком много записей в резервной копии');
  for (const annotation of parsed.payload.annotations) {
    assertStringLength(annotation.comment, 100000, 'comment');
    assertStringLength(annotation.quoteEn, 100000, 'quoteEn');
  }
  return mergeDefaults(parsed.payload, contentVersion);
}

export function downloadText(filename: string, content: string, type = 'application/json;charset=utf-8') {
  const blob = new Blob([content], { type });
  downloadBlob(filename, blob);
}

export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function makeId(prefix: string): string {
  return randomId(prefix);
}

export async function loadDraft<T>():Promise<T|null>{return transaction<T|null>('readonly',(store,done,fail)=>{const r=store.get('note-draft');r.onsuccess=()=>done(r.result??null);r.onerror=()=>fail(r.error??new Error('Черновик недоступен'));});}
export async function saveDraft(value:unknown):Promise<void>{return transaction<void>('readwrite',(store,done,fail)=>{const r=store.put(value,'note-draft');r.onsuccess=()=>done(undefined);r.onerror=()=>fail(r.error??new Error('Черновик не сохранён'));});}

export async function listCheckpoints():Promise<{key:string;revision:number;annotations:number;vocabulary:number}[]>{
 return transaction('readonly',(store,done,fail)=>{const result:{key:string;revision:number;annotations:number;vocabulary:number}[]=[];const cursor=store.openCursor();cursor.onsuccess=()=>{const c=cursor.result;if(!c){done(result);return;}const key=String(c.key);if(key.startsWith('checkpoint-')&&!key.endsWith('-draft')){const s=c.value as UserState;result.push({key,revision:s.revision,annotations:s.annotations.length,vocabulary:s.vocabulary.length});}c.continue();};cursor.onerror=()=>fail(cursor.error??new Error('Контрольные копии недоступны'));});
}
export async function readCheckpoint(key:string):Promise<UserState>{
 if(!key.startsWith('checkpoint-')||key.endsWith('-draft'))throw new Error('Некорректный ключ копии');
 return transaction('readonly',(store,done,fail)=>{const r=store.get(key);r.onsuccess=()=>{if(!r.result){fail(new Error('Копия не найдена'));return;}const state=r.result as UserState;const d=store.get(`${key}-draft`);d.onsuccess=()=>done({...state,...(d.result?{pendingDraft:d.result}:{})});d.onerror=()=>fail(d.error??new Error('Не удалось прочитать черновик'));};r.onerror=()=>fail(r.error??new Error('Не удалось прочитать копию'));});
}

